import { ResizeObject, XYCoordinates, DockingBounds } from "./types";

// The following variables refers to last known state of
// group mask, mouse down position and the resize object.
let oldBounds: DockingBounds = { left: 0, right: 0 };
let resizeObject: ResizeObject = {};
let mouseDownPosition: XYCoordinates = { x: 0, y: 0 };

/**
 * Caches the passed 3 arguments which are the old and last known group mask bounds
 * the mouse down position and the resize object.
 * @param {object} initialBounds The current group mask bounds (width, height, top, left)
 * @param {object} mousePosition The onMouseDown mouse's position (top, left)
 * @param {object} resizeObj The resize object, we need from this object the movingEdges property
 * @returns {object}
 */
export const mouseDown = (
	initialBounds: DockingBounds,
	mousePosition: { x: number; y: number },
	resizeObj: ResizeObject
) => {
	oldBounds = initialBounds || {};
	mouseDownPosition = mousePosition;
	resizeObject = resizeObj;
	return {
		oldBounds,
		mouseDownPosition,
		resizeObj,
	};
};

/**
 * Takes the latest mouse position and calculates where and how many pixels
 * should the mask be shifted, returning the newly calculated bounds as an object
 */
export const mousePosToBounds = (mousePosition: { x: number; y: number }): DockingBounds & { name: string } => {
	// The next 2 variables determine whether we are modifying the width, height, or both
	const noHorResize = !resizeObject.movingEdges?.left && !resizeObject.movingEdges?.right;
	const noVertResize = !resizeObject.movingEdges?.top && !resizeObject.movingEdges?.bottom;
	const verticalDelta = mousePosition.y - mouseDownPosition.y;
	const horizontalDelta = mousePosition.x - mouseDownPosition.x;
	let width = (oldBounds.width ?? 0) + (noHorResize ? 0 : horizontalDelta);
	let height = (oldBounds.height ?? 0) + (noVertResize ? 0 : verticalDelta);
	let top = oldBounds.top ?? 0;
	let left = oldBounds.left ?? 0;
	let right = left + width;
	let bottom = top + height;

	// In the following code, we *2 because we initially added those
	// pixels above, `(oldBounds.height ?? 0) + (noVertResize ? 0 : verticalDelta);`

	// We are changing the top position of the mask if movingEdges.top is true
	if (resizeObject.movingEdges?.top) {
		top = top + verticalDelta;
		const direction = mousePosition.y > mouseDownPosition.y ? -1 : 1;
		height = height + Math.abs(verticalDelta * 2) * direction;
	}
	// We are changing the left position of the mask if movingEdges.left is true
	if (resizeObject.movingEdges?.left) {
		left = left + horizontalDelta;
		const direction = mousePosition.x > mouseDownPosition.x ? -1 : 1;
		width = width + Math.abs(horizontalDelta * 2) * direction;
	}

	right = left + width;
	bottom = top + height;

	return {
		width,
		height,
		top,
		left,
		name: "groupMask",
		right,
		bottom,
	};
};
